/**
 * Focus drawn above the wall: one solid window per presented frame, the
 * dimmed backdrop and the Back pill; a docked Connect card shows neither, so
 * the wall stays usable beside it. The region is not modal: the shell,
 * input bar and chat stay usable, and compact focus over the desktop lets
 * clicks outside its windows through (Part 05 decisions 8 and 9). A preview
 * grown from its wall card keeps drawing after it closed, inert, until its
 * window has returned into the card (Part 10 Rule 3).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { useFloatingFrameStore } from "@/desktop/floating-frame-store";
import { useT } from "@/i18n";
import { focusMessages } from "@/i18n/messages/focus";
import { watchAgentContentRefit } from "@/lib/focus/agent-content-refit";
import {
  closeToCard,
  finishCardReturn,
  grownFrameId,
  useClosingPresentation,
} from "@/lib/focus/focus-card-return";
import { isDockedSize } from "@/lib/focus/focus-container-boxes";
import { presentedWindows } from "@/lib/focus/focus-windows";
import { useAboveWall } from "@/lib/focus/use-above-wall";
import { useFocusVisibleHere } from "@/lib/focus/use-focus-visible-here";
import {
  sameFocusSnapPreview,
  type FocusSnapPreview,
} from "@/lib/frame-window-gestures";
import { exitPresentationMode } from "@/lib/presentation-mode";
import { useDesktopMode } from "@/stores/desktop-mode-store";
import { usePresentationStore } from "@/stores/presentation-store";
import { FocusBackPill } from "./focus-back-pill";
import { FocusSnapPreviewBox } from "./focus-snap-preview";
import { FocusWindow } from "./focus-window";
import "./focus-overlay.css";

interface FocusOverlayProps {
  /** Focus closed: give keyboard focus back to the card it opened from. */
  onReturnFocus?: (frameId: string) => void;
}

export function FocusOverlay({ onReturnFocus }: FocusOverlayProps) {
  const visibleHere = useFocusVisibleHere();
  const presentation = usePresentationStore((state) => state.presentation);
  const compactFrameOverlayIds = useDesktopMode(
    (state) => state.compactFrameOverlayIds,
  );
  const [snapPreview, setSnapPreview] = useState<FocusSnapPreview | null>(null);
  const updateSnapPreview = useCallback(
    (next: FocusSnapPreview | null) =>
      setSnapPreview((current) =>
        sameFocusSnapPreview(current, next) ? current : next,
      ),
    [],
  );
  const backdropPressRef = useRef(false);
  const drawn =
    visibleHere && presentation?.renderer === "overlay" ? presentation : null;
  const closing = useClosingPresentation();
  const returning = !drawn && closing?.renderer === "overlay" ? closing : null;
  const shown = drawn ?? returning;
  const grownId = grownFrameId(shown);
  const backdropRef = useRef<HTMLDivElement>(null);
  const growProgress = useCallback((progress: number) => {
    const backdrop = backdropRef.current;
    if (backdrop)
      backdrop.style.opacity =
        progress >= 1 ? "" : String(Math.min(1, Math.max(0, progress)));
  }, []);
  // Over the desktop (compact, peek) only the overlay frames float.
  const aboveWall = useAboveWall();
  // Docked prompts (Connect cards) sit at the side without dimming the rest.
  const docked =
    !!shown &&
    shown.frameIds.length > 0 &&
    shown.frameIds.every((id) => isDockedSize(shown.naturalSizes[id]));
  const blocksWall = aboveWall && !docked;
  // A presented frame dragged onto another display is drawn by the floating
  // layer; its focus window steps aside.
  const floatingIds = useFloatingFrameStore(
    useShallow((state) => Object.keys(state.frames)),
  );
  const windows = useMemo(() => {
    if (returning) {
      // Only the window grown from the card returns into it.
      return presentedWindows(returning).filter(
        ({ frameId }) => frameId === grownId,
      );
    }
    return drawn
      ? presentedWindows(
          drawn,
          aboveWall ? undefined : new Set(compactFrameOverlayIds),
        ).filter(({ frameId }) => !floatingIds.includes(frameId))
      : [];
  }, [
    aboveWall,
    compactFrameOverlayIds,
    drawn,
    floatingIds,
    grownId,
    returning,
  ]);
  const activeId = drawn?.frameIds[drawn.activeIndex] ?? null;
  // Nothing left to return (its window is gone): the card shows at once.
  const returnsNothing = !!returning && windows.length === 0;
  useEffect(() => {
    if (returnsNothing) finishCardReturn();
  }, [returnsNothing]);
  // Gone mid-return (the wall left): the card shows again.
  useEffect(() => finishCardReturn, []);

  // Keyboard focus goes back to the card it opened from once the card shows
  // again; a new presentation over a returning one takes it instead.
  const isReturning = !!returning;
  const openingIdRef = useRef<string | null>(null);
  const focusBackIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (activeId) {
      openingIdRef.current ??= activeId;
      focusBackIdRef.current = null;
      return;
    }
    focusBackIdRef.current ??= openingIdRef.current;
    openingIdRef.current = null;
    if (isReturning) return;
    const focusBackId = focusBackIdRef.current;
    focusBackIdRef.current = null;
    if (focusBackId) onReturnFocus?.(focusBackId);
  }, [activeId, isReturning, onReturnFocus]);
  const t = useT(focusMessages);

  if (!shown) return null;
  return (
    <div
      role="region"
      aria-label={aboveWall ? t("previewRegion") : t("focusRegion")}
      className="pointer-events-none absolute inset-0"
      inert={!!returning || undefined}
      data-testid="focus-overlay"
      onPointerDownCapture={() => {
        backdropPressRef.current = false;
      }}
    >
      {blocksWall && (
        <div
          ref={backdropRef}
          className="denker-focus-backdrop pointer-events-auto absolute inset-0"
          // A card's grow drives the dim itself (`growProgress`).
          data-card-grow={grownId ? "" : undefined}
          // Not aria-hidden: desktop click-through skips hidden surfaces and
          // passes clicks to the apps behind except over marked ones; a click
          // here goes back to the wall. It holds no focusable content.
          role="presentation"
          data-interactive="true"
          data-testid="focus-backdrop"
          onPointerDown={(event) => {
            backdropPressRef.current = event.target === event.currentTarget;
          }}
          onPointerUp={(event) => {
            const pressed = backdropPressRef.current;
            backdropPressRef.current = false;
            if (pressed && event.target === event.currentTarget)
              closeToCard(exitPresentationMode);
          }}
        />
      )}
      {windows.map(({ frameId, box, active }) => (
        <FocusWindow
          key={frameId}
          frameId={frameId}
          box={box}
          active={active}
          onSnapPreview={updateSnapPreview}
          onBodyMount={watchAgentContentRefit}
          motion={shown.motion?.frameId === frameId ? shown.motion : undefined}
          returning={!!returning}
          onGrowProgress={growProgress}
          onReturned={finishCardReturn}
        />
      ))}
      {drawn && snapPreview && <FocusSnapPreviewBox preview={snapPreview} />}
      {drawn && blocksWall && activeId && <FocusBackPill frameId={activeId} />}
    </div>
  );
}
